import { useTranslation } from '../../../shared/i18n';
import React, { useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { userPath } from '../../../shared/config/paths';
import {
  Collapse,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Alert,
  Typography,
  Tooltip,
  Divider,
  Grid
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  GlobalOutlined,
  CopyOutlined,
  LockOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import confirmDnsTakeover from './confirmDnsTakeover';
import { parseOrigins } from '../../../shared/utils/format';
import { DurationInput } from '../../../shared/components/cdn/UnitInputs';

const { Option } = Select;
const { Text } = Typography;
const { useBreakpoint } = Grid;

export default function OriginSettings() {
  const { t } = useTranslation();
  const { domainData, reloadDomain } = useOutletContext();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const parentDomain = domainData?.domain || '';

  // 从完整主机名中去掉主域名后缀，只保留前缀部分
  // www.example.com → www, *.example.com → *, example.com → @
  const stripDomain = (hostname) => {
    if (!hostname || !parentDomain) return hostname;
    if (hostname === parentDomain) return '@';
    const suffix = '.' + parentDomain;
    if (hostname.endsWith(suffix)) {
      return hostname.slice(0, -suffix.length);
    }
    return hostname;
  };

  // 从前缀还原为完整主机名
  // www → www.example.com, * → *.example.com, @ → example.com
  const toFullHostname = (prefix) => {
    if (!prefix || !parentDomain) return prefix;
    const trimmed = prefix.trim();
    if (trimmed === '@') return parentDomain;
    // 用户可能输入了完整域名，去掉重复后缀
    if (trimmed === parentDomain) return parentDomain;
    const suffix = '.' + parentDomain;
    if (trimmed.endsWith(suffix)) {
      return trimmed; // 已经是完整的
    }
    return trimmed + suffix;
  };

  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingHostname, setEditingHostname] = useState(null);
  const [form] = Form.useForm();


  const copyCname = (value) => {
    if (!value) return;
    // 兼容非 HTTPS 环境
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(value)
        .then(() => message.success(t('Copied to clipboard')))
        .catch(() => message.error(t('Failed to copy. Please copy manually.')));
    } else {
      // Fallback for HTTP
      const textArea = document.createElement('textarea');
      textArea.value = value;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success(t('Copied to clipboard'));
      } catch (err) {
        message.error(t('Failed to copy. Please copy manually.'));
      }
      document.body.removeChild(textArea);
    }
  };

  const handleAdd = () => {
    form.resetFields();
    form.setFieldsValue({ origins: [{ address: '', port: undefined }] });
    setEditingHostname(null);
    setModalVisible(true);
  };

  // 端口不单独填:地址里可写 地址:端口;等于协议默认端口(http 80 / https 443 / auto 随 SSL 模式)时不保存
  const defaultPortFor = (protocol) => {
    if (protocol === 'https') return 443;
    if (protocol === 'http') return 80;
    return ['full', 'full_strict', 'strict'].includes(domainData?.ssl_mode) ? 443 : 80;
  };
  const splitHostPort = (raw) => {
    let h = String(raw || '').trim().replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
    const v6 = h.match(/^\[([^\]]+)\](?::(\d+))?$/);
    if (v6) return { host: `[${v6[1]}]`, port: v6[2] ? Number(v6[2]) : null };
    if ((h.match(/:/g) || []).length === 1) {
      const [a, p] = h.split(':');
      if (/^\d+$/.test(p)) return { host: a, port: Number(p) };
    }
    return { host: h, port: null };
  };
  const joinHostPort = (host, port) => (port ? `${host}:${port}` : host);

  // 将 origin 数据转换为 origins 列表格式（兼容新旧格式）
  const parseOriginToList = (origin, port) => {
    if (!origin) return [{ address: '', port: undefined, weight: undefined }];
    // 新格式: [{address, port, weight}, ...]
    if (Array.isArray(origin) && origin.length > 0 && typeof origin[0] === 'object' && origin[0].address) {
      return origin.map(o => ({ address: joinHostPort(o.address, o.port), weight: o.weight || undefined }));
    }
    // 旧格式: 字符串数组或换行分隔字符串
    // 兼容历史遗留的 "['1.1.1.1']"（Python repr）/ JSON 字符串
    const list = parseOrigins(origin);
    return list.length > 0
      ? list.map(addr => ({ address: joinHostPort(addr, port), weight: undefined }))
      : [{ address: '', port: undefined, weight: undefined }];
  };

  const handleEdit = (hostname) => {
    setEditingHostname(hostname);
    form.setFieldsValue({
      ...hostname,
      hostname: stripDomain(hostname.hostname),
      origins: parseOriginToList(hostname.origin, hostname.port),
      // 空 = 跟随域名「网络设置」
      http_version: hostname.http_version || '',
      proxy_timeout: hostname.proxy_timeout || undefined,
      websocket_enabled: hostname.websocket_enabled == null ? '' : (hostname.websocket_enabled ? 'on' : 'off'),
    });
    setModalVisible(true);
  };

  const handleDelete = async (hostnameId) => {
    setLoading(true);
    try {
      const response = await cdnService.deleteHostname(hostnameId);

      if (response.success) {
        message.success(t('Deleted successfully'));
        reloadDomain();
      } else {
        message.error(response.error || t('Deletion failed'));
      }
    } catch (error) {
      console.error('Failed to delete hostname:', error);
      message.error(t('Deletion failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // 新格式: origins 列表 [{address, port}, ...]
      const defPort = defaultPortFor(values.protocol);
      const originsList = (values.origins || []).filter(o => o && o.address).map(o => {
        const { host, port } = splitHostPort(o.address);
        return { ...o, address: host, port: port && port !== defPort ? port : null };
      });
      if (originsList.length === 0) {
        message.error(t('Please add at least one origin address'));
        setLoading(false);
        return;
      }
      // 检查是否有自定义权重或不同端口
      const hasCustomWeight = originsList.some(o => o.weight && o.weight !== 100);
      const allSamePort = originsList.every(o => (o.port || null) === (originsList[0].port || null));
      let originData;
      if (!hasCustomWeight && allSamePort && originsList.length === 1) {
        originData = originsList[0].address;
      } else if (!hasCustomWeight && allSamePort) {
        originData = originsList.map(o => o.address);
      } else {
        // 使用新的 {address, port, weight} 格式
        originData = originsList.map(o => {
          const entry = { address: o.address, port: o.port || null };
          if (o.weight && o.weight !== 100) entry.weight = o.weight;
          return entry;
        });
      }

      const data = {
        ...values,
        hostname: toFullHostname(values.hostname),
        origin: originData,
        port: allSamePort ? (originsList[0].port || null) : null,
        // 留空 = 跟随域名网络设置(显式发 null,否则清空后后端收不到、旧值不变)
        http_version: values.http_version || '',
        proxy_timeout: values.proxy_timeout || null,
        websocket_enabled: values.websocket_enabled ? values.websocket_enabled === 'on' : null,
        domain_id: domainData.id
      };
      // 移除 origins 字段（已转换为 origin）
      delete data.origins;

      let response;
      if (editingHostname) {
        response = await cdnService.updateHostname({
          ...data,
          id: editingHostname.id
        });
      } else {
        if (!(await confirmDnsTakeover([data.hostname]))) return;
        response = await cdnService.createHostname({ ...data, confirm_dns: 1 });
      }

      if (response.success) {
        message.success(editingHostname ? t('Updated successfully') : t('Added successfully'));
        setModalVisible(false);
        reloadDomain();
      } else {
        message.error(response.error || t('Operation failed'));
      }
    } catch (error) {
      console.error('Failed to save hostname:', error);
      message.error(t('Operation failed'));
    } finally {
      setLoading(false);
    }
  };

  // SSL 统一在 SSL/TLS 页管理(含证书夹),这里只跳转并自动打开该主机名
  const goSsl = (hostname) => navigate(userPath(`/cdn/domains/${domainData.domain}/ssl?hostname=${hostname.id}`));

  const getSslStatusTag = (hostname) => {
    const status = hostname.ssl_status;
    if (!status || !status.enabled) {
      return <Tag>{t('Disabled')}</Tag>;
    }

    const acmeStatus = status.acme_status;
    if (status.type === 'custom') {
      return <Tag color="green" icon={<CheckCircleOutlined />}>{t('Custom certificate')}</Tag>;
    }

    switch (acmeStatus) {
      case 'pending':
        return <Tag color="processing" icon={<LoadingOutlined />}>{t('Waiting')}</Tag>;
      case 'verifying':
        return <Tag color="processing" icon={<SyncOutlined spin />}>{t('Verifying')}</Tag>;
      case 'issuing':
        return <Tag color="processing" icon={<SyncOutlined spin />}>{t('Issuing')}</Tag>;
      case 'issued':
        return <Tag color="success" icon={<CheckCircleOutlined />}>{t('Issued')}</Tag>;
      case 'failed':
        return <Tag color="error" icon={<CloseCircleOutlined />}>{t('Failed')}</Tag>;
      default:
        return <Tag color="blue" icon={<LockOutlined />}>{t('Enabled')}</Tag>;
    }
  };

  // 桌面端表格列配置（精简，详细配置在编辑弹窗中查看）
  const columns = [
    {
      title: t('Hostname'),
      dataIndex: 'hostname',
      key: 'hostname',
      width: 120,
      render: (text) => {
        const prefix = stripDomain(text);
        return (
          <Tooltip title={text}>
            <strong style={{ whiteSpace: 'nowrap' }}>{prefix}</strong>
          </Tooltip>
        );
      }
    },
    {
      title: t('Origin address'),
      dataIndex: 'origin',
      key: 'origin',
      width: 200,
      render: (origin, record) => {
        const origins = Array.isArray(origin) ? origin : [origin];
        const items = origins.filter(Boolean).map((o, i) => {
          if (typeof o === 'object' && o.address) {
            return { address: o.address, port: o.port || record.port, weight: o.weight };
          }
          return { address: String(o), port: record.port };
        });
        if (items.length === 0) return <Text type="secondary">{t('Not configured')}</Text>;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {items.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{
                  display: 'inline-block',
                  width: 6, height: 6,
                  borderRadius: '50%',
                  background: '#52c41a',
                  flexShrink: 0
                }} />
                <Text style={{ fontSize: 13 }}>
                  {item.address}{item.port ? <Text type="secondary">:{item.port}</Text> : null}
                  {item.weight && item.weight !== 100 ? <Text type="secondary"> (w:{item.weight})</Text> : null}
                </Text>
              </div>
            ))}
          </div>
        );
      }
    },
    {
      title: 'CNAME',
      key: 'cname_target',
      width: 220,
      render: (_, record) => record.cname_target ? (
        <Space size={4} style={{ whiteSpace: 'nowrap' }}>
          <Text code style={{ fontSize: 11 }}>{record.cname_target}</Text>
          <Tooltip title={t('Copy')}>
            <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copyCname(record.cname_target)} style={{ padding: 0 }} />
          </Tooltip>
        </Space>
      ) : (
        <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>{t('Generated after saving')}</Text>
      )
    },
    {
      title: t('Status'),
      dataIndex: 'enabled',
      key: 'enabled',
      width: 70,
      render: (enabled) => (
        <Tag color={enabled ? 'success' : 'default'}>
          {enabled ? t('Enable') : t('Disable')}
        </Tag>
      )
    },
    {
      title: 'SSL',
      key: 'ssl',
      width: 100,
      render: (_, record) => getSslStatusTag(record)
    },
    {
      title: t('Actions'),
      key: 'action',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size={0}>
          <Button type="link" size="small" icon={<LockOutlined />} onClick={() => goSsl(record)}>
            SSL
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>{t('Edit')}</Button>
          <Popconfirm
            title={t('Delete this origin?')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('OK')}
            cancelText={t('Cancel')}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>{t('Delete')}</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // 移动端卡片渲染
  const renderMobileCard = (item) => (
    <Card
      key={item.id}
      size="small"
      style={{ marginBottom: 12, overflow: 'hidden' }}
      title={
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          overflow: 'hidden',
          width: '100%'
        }}>
          <Tooltip title={item.hostname}>
            <span style={{
              fontSize: 14,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {stripDomain(item.hostname)}
            </span>
          </Tooltip>
          <Tag color={item.enabled ? 'success' : 'default'} style={{ marginLeft: 8, flexShrink: 0 }}>
            {item.enabled ? t('Enable') : t('Disable')}
          </Tag>
        </div>
      }
      styles={{
        header: { padding: '8px 12px' },
        body: { padding: '12px', overflow: 'hidden' }
      }}
    >
      <div style={{ fontSize: 12, marginBottom: 8 }}>
        <div style={{ color: '#999', marginBottom: 4, fontSize: 11 }}>{t('Origin address')}</div>
        <div style={{ background: '#f5f5f5', padding: '6px 10px', borderRadius: 6 }}>
          {(() => {
            const origins = Array.isArray(item.origin) ? item.origin : parseOrigins(item.origin);
            const items = origins.filter(Boolean).map(o => {
              if (typeof o === 'object' && o.address) return { address: o.address, port: o.port || item.port, weight: o.weight };
              return { address: String(o), port: item.port };
            });
            return items.map((o, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 0' }}>
                <span style={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', background: '#52c41a', flexShrink: 0 }} />
                <span style={{ wordBreak: 'break-all' }}>
                  {o.address}{o.port ? <span style={{ color: '#999' }}>:{o.port}</span> : null}
                  {o.weight && o.weight !== 100 ? <span style={{ color: '#999' }}> (w:{o.weight})</span> : null}
                </span>
              </div>
            ));
          })()}
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        <Tag color="blue">{item.protocol || 'auto'}</Tag>
        {getSslStatusTag(item)}
      </div>
      {item.cname_target && (
        <div style={{ fontSize: 12, marginBottom: 8 }}>
          <div style={{ color: '#666', marginBottom: 4 }}>CNAME:</div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            background: '#f5f5f5',
            padding: '4px 8px',
            borderRadius: 4,
            overflow: 'hidden'
          }}>
            <span style={{
              fontSize: 11,
              wordBreak: 'break-all',
              flex: 1,
              minWidth: 0,
              fontFamily: 'monospace'
            }}>
              {item.cname_target}
            </span>
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyCname(item.cname_target)}
              style={{ padding: 0, flexShrink: 0 }}
            />
          </div>
        </div>
      )}
      <Divider style={{ margin: '8px 0' }} />
      <Space wrap size="small">
        <Button size="small" icon={<LockOutlined />} onClick={() => goSsl(item)}>
          SSL
        </Button>
        <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(item)}>{t('Edit')}</Button>
        <Popconfirm
          title={t('Delete this origin?')}
          onConfirm={() => handleDelete(item.id)}
          okText={t('OK')}
          cancelText={t('Cancel')}
        >
          <Button size="small" danger icon={<DeleteOutlined />}>{t('Delete')}</Button>
        </Popconfirm>
      </Space>
    </Card>
  );

  return (
    <div style={{ padding: isMobile ? '12px 0' : '24px 0' }}>
      <Card
        title={
          <Space>
            <GlobalOutlined />
            <span style={{ fontSize: isMobile ? 14 : 16 }}>{t('Origin settings')}</span>
          </Space>
        }
        size={isMobile ? 'small' : 'default'}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? t('Add') : t('Add origin')}
          </Button>
        }
      >
        <Alert
          message={t('Configuration instructions')}
          description={isMobile
            ? t('The CDN fetches content from the origin, caches it, and delivers it to users.')
            : t('Origins are your actual server addresses. CDN fetches content from these origins, caches it, and delivers it to users. You can configure multiple origins for load balancing and failover.')
          }
          type="info"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />

        {isMobile ? (
          // 移动端使用卡片列表
          <div>
            {(domainData.hostnames || []).map(item => renderMobileCard(item))}
            {(!domainData.hostnames || domainData.hostnames.length === 0) && (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>{t('No origin configuration yet')}</div>
            )}
          </div>
        ) : (
          // 桌面端使用表格
          <Table
            columns={columns}
            dataSource={domainData.hostnames || []}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={{ x: 870 }}
            size="small"
          />
        )}
      </Card>

      {/* 添加/编辑对话框 */}
      <Modal
        title={editingHostname ? t('Edit origin') : t('Add origin')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={loading}
        width={isMobile ? '100%' : 700}
        style={isMobile ? { top: 20 } : undefined}
        styles={isMobile ? { body: { maxHeight: '70vh', overflow: 'auto' } } : undefined}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          size={isMobile ? 'middle' : 'default'}
          initialValues={{
            protocol: 'auto',
            mode: 'roundrobin',
            http_version: '',
            websocket_enabled: '',
            enabled: true,
            priority: 100
          }}
        >
          <Form.Item
            name="hostname"
            label={t('Hostname')}
            rules={[{ required: true, message: t('Please enter a hostname') }]}
            extra={t('Enter @ for the root domain or * for a wildcard domain')}
          >
            <Input
              placeholder="www"
              addonAfter={<span style={{ fontSize: 12 }}>.{parentDomain}</span>}
            />
          </Form.Item>

          <Form.Item label={t('Origin address')} required>
            <Form.List
              name="origins"
              initialValue={[{ address: '', port: undefined }]}
              rules={[{
                validator: async (_, origins) => {
                  if (!origins || origins.length === 0 || !origins.some(o => o && o.address)) {
                    return Promise.reject(new Error(t('Please add at least one origin address')));
                  }
                }
              }]}
            >
              {(fields, { add, remove }, { errors }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} style={{ display: 'flex', marginBottom: 8, width: '100%' }} align="baseline">
                      <Form.Item
                        {...restField}
                        name={[name, 'address']}
                        rules={[{ required: true, message: t('Please enter an address') }]}
                        style={{ flex: 1, marginBottom: 0 }}
                      >
                        <Input placeholder={t('IP or domain, e.g. 1.2.3.4:8080')} style={{ width: isMobile ? 160 : 280 }} />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'weight']}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber min={1} max={100} placeholder={t('Weight')} style={{ width: isMobile ? 70 : 80 }} />
                      </Form.Item>
                      {fields.length > 1 && (
                        <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                      )}
                    </Space>
                  ))}
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} size={isMobile ? 'small' : 'middle'}>{t('Add origin address')}</Button>
                  </Form.Item>
                  <Form.ErrorList errors={errors} />
                </>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item
            name="protocol"
            label={t('Origin protocol')}
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="auto">{t('Automatic (follow SSL encryption mode)')}</Option>
              <Option value="http">HTTP</Option>
              <Option value="https">HTTPS</Option>
            </Select>
          </Form.Item>

          <Form.Item name="mode" label={t('Load balancing mode')}>
            <Select>
              <Option value="roundrobin">{t('Round robin')}</Option>
              <Option value="ip_hash">IP Hash</Option>
              <Option value="least_conn">{t('Least connections')}</Option>
            </Select>
          </Form.Item>

          <Form.Item name="http_version" label={t('Origin HTTP version')} extra={t('HTTP version used by the CDN to connect to your origin server')}>
            <Select>
              <Option value="">{t('Follow domain setting')}</Option>
              <Option value="1.0">HTTP/1.0</Option>
              <Option value="1.1">HTTP/1.1</Option>
              <Option value="2">HTTP/2</Option>
            </Select>
          </Form.Item>

          {/* HTTP/2 回源隐藏依赖提示:H2 需 TLS 协商(ALPN),而 flexible/off 模式回源为明文,H2 静默失效 */}
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.http_version !== cur.http_version}>
            {({ getFieldValue }) => {
              const hv = String(getFieldValue('http_version') || domainData?.http_version || '');
              const mode = domainData?.ssl_mode || 'full';
              if (hv.includes('2') && (mode === 'flexible' || mode === 'off')) {
                return (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t('HTTP/2 origin requests are not supported in the current SSL mode')}
                    description={t('The current SSL encryption mode is {{value}}. The CDN connects to the origin in plaintext, so HTTP/2 cannot be negotiated via TLS ALPN. Origin requests fall back to HTTP/1.1. To use HTTP/2 to the origin, change the encryption mode to Full or Strict in SSL settings.', { value: mode === 'off' ? t('Off') : t('Flexible') })}
                  />
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item name="websocket_enabled" label={t('WebSocket support')}>
            <Select>
              <Option value="">{t('Follow domain setting')}</Option>
              <Option value="on">{t('Enable')}</Option>
              <Option value="off">{t('Close')}</Option>
            </Select>
          </Form.Item>

          <Form.Item name="proxy_timeout" label={t('Proxy timeout (seconds)')}>
            <DurationInput presets={[10, 30, 60, 120, 300, 600]} min={1} max={3600} emptyLabel={t('Follow domain setting')} />
          </Form.Item>

          <Form.Item name="priority" label={t('Priority')} extra={t('Lower values have higher priority')}>
            <InputNumber min={1} max={999} style={{ width: '100%' }} />
          </Form.Item>

          <Collapse
            ghost
            size="small"
            style={{ marginBottom: 16, marginLeft: -16 }}
            items={[{
              key: 'adv',
              label: t('Advanced'),
              forceRender: true,
              children: (
                <>
                  <Form.Item name="origin_host" label={t('Origin Host')} tooltip={t('Host header sent to the origin; leave blank to use the requested hostname')}>
                    <Input placeholder={t('Leave blank to use the requested hostname')} allowClear />
                  </Form.Item>
                  <Form.Item name="origin_sni" label={t('Origin SNI')} tooltip={t('TLS SNI for HTTPS origin requests; if blank, use the origin Host; if that is also blank, use the requested hostname')}>
                    <Input placeholder={t('Leave blank to use the origin Host')} allowClear />
                  </Form.Item>
                </>
              ),
            }]}
          />

          <Form.Item name="enabled" label={t('Enable')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
}
